import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { map, Observable } from 'rxjs';
import { selectBffBaseUrl } from '../../store/selectors/api-config.selectors';
import { APIService, HttpParamsObject } from './api.service';
import {
  ConsentPermissionResponse,
  ConsentPermissionRequest,
  ConsentPolicy,
} from '../consent/consent.interface';

@Injectable({ providedIn: 'root' })
export class ConsentApiService {
  private basePath: string;
  private bff: string;

  constructor(
    private apiService: APIService,
    private store: Store,
  ) {
    this.store.pipe(selectBffBaseUrl).subscribe((url) => {
      this.bff = url;
      this.basePath = `${url}/service`;
    });
  }

  public readPermission(
    permissionId: string,
    patientId: string,
  ): Observable<ConsentPermissionResponse> {
    return this.apiService.callGetAPI<ConsentPermissionResponse>(
      `${this.basePath}/permission/patient/${patientId}/${permissionId}`,
    );
  }

  public updatePermission(
    permissionId: string,
    permissionRequest: ConsentPermissionRequest,
  ): Observable<ConsentPermissionResponse> {
    return this.apiService.callPutAPI<ConsentPermissionResponse>(
      `${this.basePath}/permission/${permissionId}`,
      permissionRequest,
    );
  }

  public deletePermission(permission): Observable<any> {
    return this.apiService.callDeleteAPI(
      `${this.bff}/service/consent/id/${permission.id}`,
    );
  }

  public createPermission(
    permissionRequest: ConsentPermissionRequest,
  ): Observable<ConsentPermissionResponse> {
    return this.apiService.callPostAPI<ConsentPermissionResponse>(
      `${this.basePath}/consent/individual`,
      permissionRequest,
    );
  }

  public searchPermissions(
    patientId: string,
    referencedContextLabel?: string,
    _includeDeprecated?: boolean,
  ): Observable<ConsentPermissionResponse[]> {
    const paramsObject: HttpParamsObject = {};
    if (referencedContextLabel)
      paramsObject.referencedContextLabel = referencedContextLabel;

    return this.apiService.callGetAPI<ConsentPermissionResponse[]>(
      `${this.basePath}/permission/patient/${patientId}`,
      paramsObject,
    );
  }

  public searchPolicies(contextLabel: string): Observable<ConsentPolicy[]> {
    const paramsObject: HttpParamsObject = {};
    if (contextLabel) paramsObject.contextLabel = contextLabel;

    return this.apiService.callGetAPI<ConsentPolicy[]>(
      `${this.basePath}/policy/general/base`,
      paramsObject,
    );
  }

  public getUserFacilitiesOidsFromPermisions$(
    patientId: string,
  ): Observable<string> {
    return this.apiService
      .callGetAPI(`${this.basePath}/permission/patient/${patientId}`)
      .pipe(
        map((permissions: ConsentPermissionResponse[]) =>
          permissions
            .map(
              (permission: ConsentPermissionResponse) =>
                permission.facilities?.[0],
            )
            .filter(Boolean)
            .join(','),
        ),
      );
  }
}
